const IdleJs = require('idle-js');

// Reuse a single immutable events array to avoid reallocations per call.
const DEFAULT_EVENTS = Object.freeze([
    'mousemove',
    'keydown',
    'mousedown',
    'touchstart',
    'scroll'
]);

/**
 * @callback OnIdleCallback
 * A function called when the user becomes idle.
 * @returns {void}
 */

/**
 * Initializes an idle-time observer that listens for user inactivity.
 *
 * @param {Object} config Configuration object.
 * @param {number} config.idleTime Time in milliseconds before the user is
 *  considered idle. (required)
 * @param {boolean} [config.keepTracking=true] If true, the callback is
 *  triggered on every idle state change. (optional)
 * @param {OnIdleCallback} config.onIdle Callback executed when the user becomes
 *  idle. (required)
 * @returns {Object} The started IdleJs instance, so the caller can stop/destroy
 *  it when needed.
 *
 * @throws {TypeError} If required parameters are missing or of the wrong type.
 */
function createIdleObserver({
    idleTime,
    keepTracking = true,
    onIdle
}) {
    // Validate early for clearer errors and safer usage.
    if (typeof idleTime !== 'number' || !Number.isFinite(idleTime) || idleTime <= 0) {
        throw new TypeError('config.idleTime must be a positive finite number (milliseconds).');
    }
    if (typeof onIdle !== 'function') {
        throw new TypeError('config.onIdle must be a function.');
    }

    // Coerce to integer ms to avoid passing floats (clarity + consistency).
    const idleTimeMs = Math.trunc(idleTime);

    const idle = new IdleJs({
        idle: idleTimeMs,
        events: DEFAULT_EVENTS,
        onIdle,
        keepTracking,
        startAtIdle: false
    });

    idle.start();
    return idle;
}

module.exports = {
    createIdleObserver: createIdleObserver
};
